import type { CourtSummary, QueueListItem, TenantSettings } from "@saquei/types";
import type { SseEvent } from "./types";

export interface RecentEvent {
    id: string;
    event_type: string;
    payload: unknown;
    received_at: string;
}

export interface EventState {
    courts: Record<string, CourtSummary>;
    queue: QueueListItem[];
    settings: TenantSettings | null;
    connected: boolean;
    lastEventId: string | null;
    recentEvents: RecentEvent[];
    _needsRefetch?: boolean;
    _settingsChanged?: boolean;
}

type SessionStartedPayload = { court_id: string; started_at: string; expected_end_at?: string | null };
type SessionEndedPayload = { court_id: string };
type SessionExtendedPayload = { court_id: string; new_expected_end_at: string };
type CourtStatusChangedPayload = { court_id: string; status: string };

const MAX_RECENT_EVENTS = 20;

export function applyPatch(prev: EventState, e: SseEvent): EventState {
    const recent: RecentEvent = {
        id: e.id,
        event_type: e.event_type,
        payload: e.payload,
        received_at: new Date().toISOString(),
    };
    const recentEvents = [recent, ...prev.recentEvents].slice(0, MAX_RECENT_EVENTS);

    switch (e.event_type) {
        case "session.started": {
            const p = e.payload as SessionStartedPayload;
            const cid = p.court_id;
            const courts = { ...prev.courts };
            if (courts[cid]) {
                courts[cid] = {
                    ...courts[cid],
                    state: "ocupada",
                    session_started_at: p.started_at,
                    session_expected_end_at: p.expected_end_at ?? null,
                };
            }
            return { ...prev, courts, lastEventId: e.id, recentEvents };
        }
        case "session.ended": {
            const p = e.payload as SessionEndedPayload;
            const cid = p.court_id;
            const courts = { ...prev.courts };
            if (courts[cid]) {
                courts[cid] = {
                    ...courts[cid],
                    state: "livre",
                    session_started_at: null,
                    session_expected_end_at: null,
                    active_session_id: null,
                };
            }
            return { ...prev, courts, lastEventId: e.id, recentEvents };
        }
        case "session.extended": {
            const p = e.payload as SessionExtendedPayload;
            const cid = p.court_id;
            const courts = { ...prev.courts };
            if (courts[cid]) {
                courts[cid] = {
                    ...courts[cid],
                    session_expected_end_at: p.new_expected_end_at,
                };
            }
            return { ...prev, courts, lastEventId: e.id, recentEvents };
        }
        case "queue.entry.added":
        case "queue.entry.invited":
        case "queue.entry.passed":
        case "queue.entry.cancelled":
        case "queue.entry.expired":
        case "queue.entry.served": {
            return { ...prev, lastEventId: e.id, recentEvents, _needsRefetch: true };
        }
        case "court.status_changed": {
            const p = e.payload as CourtStatusChangedPayload;
            const cid = p.court_id;
            const courts = { ...prev.courts };
            if (courts[cid]) {
                courts[cid] = { ...courts[cid], status: p.status };
            }
            return { ...prev, courts, lastEventId: e.id, recentEvents };
        }
        case "tenant.settings_changed": {
            return { ...prev, lastEventId: e.id, recentEvents, _settingsChanged: true };
        }
        default:
            return { ...prev, lastEventId: e.id, recentEvents };
    }
}
